/**
 * Sidebar.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Module-agnostic left navigation sidebar.
 * Builds nav from loaded manifests (useModuleManifests). Shell NEVER
 * hard-codes module IDs or routes — §1.2 BINDING.
 *
 * Features:
 * - Collapse/expand (icon-only mode) — persisted to userPrefsStore
 * - Keyboard shortcut ⌘[ to toggle collapse
 * - Badge support on nav items
 * - Recents section (from recentsStore)
 * - Empty state when no modules enabled (Phase 1 correct behavior)
 * - Keyboard navigation (arrow keys between items)
 *
 * SIDEBAR-IMPORT-GATE (BINDING): every icon name in this file's lucide-react
 * import MUST resolve. Run:
 *   grep -o "import {[^}]*} from 'lucide-react'" src/shell/Sidebar.jsx
 * Zero ReferenceErrors on render.
 */
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../primitives/Skeleton/Skeleton.tsx';
import { EmptyState } from '../components/empty-state/EmptyState.jsx';

import { useModuleManifests } from './ModuleLoader.jsx';
import { RecentsSection }      from './RecentsSection.jsx';
import { SidebarItem }         from './SidebarItem.jsx';
import { WorkspaceSwitcher }   from './WorkspaceSwitcher.jsx';
import { resolveModuleIcon }   from './iconRegistry.js';
import {
  useSidebarCollapsed,
  useSidebarWidth,
  useUserPrefsStore,
} from '../stores/userPrefsStore.js';

/** Settings icon for the bottom settings link. */
const SettingsIcon = Settings;

/**
 * @param {{ className?: string }} props
 */
export function Sidebar({ className }) {
  const { manifests, loading } = useModuleManifests();

  const isCollapsed   = useSidebarCollapsed();
  const sidebarWidth  = useSidebarWidth();
  const { setSidebarCollapsed, setSidebarWidth } = useUserPrefsStore();

  const sidebarRef  = useRef(null);
  const isDragging  = useRef(false);
  const dragStartX  = useRef(0);
  const dragStartW  = useRef(0);

  // IA-001: auto-collapse below 1280px on viewport resize only (not on mount —
  // mount-time forced collapse overrides the stored/default expanded state,
  // regression introduced in ELYON-SHELL-WORLD-CLASS-1 #3549, fixed by
  // SIDEBAR-DEFAULT-STATE-1).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1279px)');
    const handler = (e) => { if (e.matches) setSidebarCollapsed(true); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setSidebarCollapsed]);

  // BEH-001: ⌘[ keyboard shortcut to toggle collapse
  useEffect(() => {
    function onKeyDown(e) {
      const isMac = navigator.platform.startsWith('Mac');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.key === '[') {
        e.preventDefault();
        setSidebarCollapsed(!isCollapsed);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isCollapsed, setSidebarCollapsed]);

  // BEH-001: keyboard arrow navigation between sidebar items
  const onSidebarKeyDown = useCallback((e) => {
    const items = sidebarRef.current?.querySelectorAll('[role="listitem"] a, [role="listitem"] button');
    if (!items) return;
    const arr = Array.from(items);
    const idx = arr.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' && idx < arr.length - 1) {
      e.preventDefault();
      arr[idx + 1].focus();
    }
    if (e.key === 'ArrowUp' && idx > 0) {
      e.preventDefault();
      arr[idx - 1].focus();
    }
  }, []);

  // BEH-001: drag-to-resize handle
  const onResizeMouseDown = useCallback((e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartW.current = sidebarWidth;
    document.body.style.cursor    = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(() => {
    function onMouseMove(e) {
      if (!isDragging.current) return;
      const delta    = e.clientX - dragStartX.current;
      const newWidth = Math.max(200, Math.min(400, dragStartW.current + delta));
      setSidebarWidth(newWidth);
    }
    function onMouseUp() {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor    = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
    };
  }, [setSidebarWidth]);

  return (
    <aside
      ref={sidebarRef}
      className={['elyon-sidebar', className].filter(Boolean).join(' ')}
      style={!isCollapsed ? { width: sidebarWidth, minWidth: sidebarWidth } : undefined}
      /* A11Y-002: landmark + label */
      role="navigation"
      aria-label="Primary navigation"
      data-testid="sidebar"
      onKeyDown={onSidebarKeyDown}
    >
      {/* Workspace switcher — tenant brand + settings menu */}
      <div className="elyon-sidebar-header">
        <WorkspaceSwitcher isCollapsed={isCollapsed} />
      </div>

      {/* Scrollable nav area — standard Radix ScrollArea pattern */}
      <ScrollArea.Root style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ScrollArea.Viewport className="elyon-sidebar-scroll">
          <nav aria-label="Module navigation">
              {loading ? (
                /* BEH-006: Skeleton primitives — no custom shimmer divs */
                <div className="elyon-sidebar-skeleton" aria-hidden="true" aria-label="Loading modules">
                  <Skeleton.Line width="72%" />
                  <Skeleton.Line width="88%" />
                  <Skeleton.Line width="60%" />
                  <Skeleton.Line width="80%" />
                  <Skeleton.Line width="68%" />
                </div>
              ) : manifests.length === 0 ? (
                /* EmptyState component — no bare text */
                !isCollapsed && (
                  <EmptyState
                    pattern="first-use"
                    headline="No modules yet"
                    body="Modules appear once tenant configuration is provisioned."
                  />
                )
              ) : (
                /* Module nav items built from manifests */
                <section
                  className="elyon-sidebar-section"
                  aria-labelledby="sidebar-modules-label"
                >
                  {!isCollapsed && (
                    <span
                      id="sidebar-modules-label"
                      className="elyon-sidebar-section-label"
                    >
                      Modules
                    </span>
                  )}
                  <ul className="elyon-sidebar-list" role="list">
                    {manifests.map((m) => {
                      const Icon = resolveModuleIcon(m.icon);
                      return (
                        <li key={m.id} role="listitem">
                          <SidebarItem
                            id={m.id}
                            label={m.label}
                            icon={Icon}
                            to={m.defaultRoute ?? m.path ?? `/${m.id}`}
                            isCollapsed={isCollapsed}
                            badge={m.badge}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* Recents — DATA-002 */}
              <RecentsSection isCollapsed={isCollapsed} />
            </nav>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical" style={{ width: 3 }}>
          <ScrollArea.Thumb style={{ background: 'var(--color-border-default)', borderRadius: 'var(--radius-full)' }} />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>

      {/* Bottom: settings link */}
      <div className="elyon-sidebar-bottom" role="contentinfo">
        <ul className="elyon-sidebar-list" role="list">
          <li role="listitem">
            <SidebarItem
              id="settings"
              label="Settings"
              icon={SettingsIcon}
              to="/settings"
              isCollapsed={isCollapsed}
            />
          </li>
        </ul>
      </div>

      {/* BEH-001: collapse toggle button */}
      <button
        className="elyon-sidebar-collapse-btn"
        onClick={() => setSidebarCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!isCollapsed}
        title={isCollapsed ? 'Expand sidebar (⌘[)' : 'Collapse sidebar (⌘[)'}
      >
        {isCollapsed
          ? <ChevronRight size={12} strokeWidth={2} aria-hidden="true" />
          : <ChevronLeft  size={12} strokeWidth={2} aria-hidden="true" />
        }
      </button>

      {/* BEH-001: drag-to-resize handle */}
      {!isCollapsed && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 4,
            cursor: 'col-resize',
            zIndex: 'var(--z-index-raised)',
          }}
          onMouseDown={onResizeMouseDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') setSidebarWidth(Math.min(400, sidebarWidth + 16));
            if (e.key === 'ArrowLeft')  setSidebarWidth(Math.max(200, sidebarWidth - 16));
          }}
        />
      )}
    </aside>
  );
}
